import PostThumb from '@components/PostThumb'
import { SocialPostFragment } from '@graphql/fragments/__generated__/SocialPost.generated'
import { useSocialPostsQuery } from '@graphql/queries/__generated__/social-posts.generated'
import { Button } from '@mui/material'
import _ from 'lodash'
import React, { Suspense, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { DOMAIN, INSTAGRAM_URL } from '../../../../constants'
import { DialogPost } from '../../../components/DialogPost'
import { INSPIRATION_LIST } from '../../../urls'
import { maybe } from '../../../utils/misc'
import { useTranslation } from 'react-i18next'
import { Ordering } from '../../../../generated/gqlTypes'

const UploadPostDialog = React.lazy(() => import('@components/UploadPostDialog/UploadPostDialog'))

const PAGINATE_BY = 30

interface InspirationProps {}

const Inspiration: React.FC<InspirationProps> = ({}) => {
  const { t } = useTranslation()
  const [isOpenUploadPost, setIsOpenUploadPost] = useState(false)
  const postsQuery = useSocialPostsQuery({
    variables: { first: PAGINATE_BY, orderBy: [{ created: Ordering.Desc }] },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  const posts: SocialPostFragment[] = useMemo(
    () =>
      maybe(
        () =>
          _.sampleSize(
            postsQuery.data.socialPosts.edges.map(edge => edge.node),
            20
          ),
        []
      ),
    [postsQuery.data]
  )
  const swiperOptions: SwiperOptions = {
    spaceBetween: 5,
    slidesPerView: 6,
    pagination: {
      clickable: true,
      el: `#swiper-pagination-posts`,
    },
    watchSlidesProgress: true,
    loop: false,
    allowSlideNext: posts.length > 1,
    allowSlidePrev: posts.length > 1,
    navigation: {
      prevEl: `#swiper-button-prev-posts`,
      nextEl: `#swiper-button-next-posts`,
    },

    breakpoints: {
      0: {
        slidesPerView: 2,
        slidesPerGroup: 2,
        speed: 300,
      },
      600: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        speed: 300,
      },
      800: {
        slidesPerView: 4,
        slidesPerGroup: 4,
        speed: 500,
      },
      1000: {
        slidesPerView: 5,
        slidesPerGroup: 5,
        speed: 500,
      },
      1200: {
        slidesPerView: 6,
        slidesPerGroup: 6,
        speed: 500,
      },
    },
  }
  return (
    <section className='my-60'>
      <div className='text-center my-30'>
        <h3 className='font-bold text-5xl font-futura'>{t('title.inspiration', 'Inspiration')}</h3>
        <div className='flex flex-wrap items-center justify-center py-20 px-20 md:px-40 text-gray text-base leading-normal'>
          <span>
            <a className='no-underline' href={INSTAGRAM_URL} target='_blank'>
              @{DOMAIN}
            </a>{' '}
            {t(
              'description.inspiration',
              '- is a place for creativity that we will show to people who are interested in art from all over the world. Just tag us on social media or add your photo for more inspiration'
            )}
          </span>
          <div className='flex items-center'>
            <Button
              className='transparent'
              variant='text'
              onClick={() => setIsOpenUploadPost(true)}
              disableRipple
              disableElevation
            >
              <span className='text-gray underline normal-case text-base'>
                {t('button.addYourPhoto', 'Add Your Photo')}
              </span>
            </Button>
            <Button
              component={Link}
              to={INSPIRATION_LIST}
              className='transparent'
              variant='text'
              disableRipple
              disableElevation
            >
              <span className='text-gray underline normal-case text-base'>
                {t('button.viewMoreInspiration', 'View More Inspiration')}
              </span>
            </Button>
          </div>
        </div>
        {posts.length > 0 && (
          <div className='relative px-10 md:px-30'>
            <Swiper {...swiperOptions}>
              {posts.map(node => {
                return (
                  <SwiperSlide key={node.id}>
                    <div className='relative w-full h-200'>
                      <PostThumb post={node} />
                    </div>
                  </SwiperSlide>
                )
              })}
            </Swiper>
            <div
              id={`swiper-pagination-posts`}
              className={`swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full ${
                posts.length > 1 ? 'block' : 'hidden'
              } md:hidden`}
            />
            <div id={`swiper-button-prev-posts`} className='swiper-button-prev swiper-button--small hidden md:block' />
            <div id={`swiper-button-next-posts`} className='swiper-button-next swiper-button--small hidden md:block' />
          </div>
        )}
      </div>
      <DialogPost />
      <Suspense fallback={null}>
        {isOpenUploadPost && <UploadPostDialog isOpen={isOpenUploadPost} onClose={() => setIsOpenUploadPost(false)} />}
      </Suspense>
    </section>
  )
}

export default Inspiration
